import React from 'react';
import {Box, Typography, CircularProgress, Tooltip} from '@mui/material';
import {NAVY, ORANGE, PageHeader, GuidanceBanner, BandChip, BandMarkerLegend, MARKER_PRIMARY, CONTEXT_BAND_COLORS, contextBandColor} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import {isSuppressedItem, isSuppressedBenchmark, whole} from '../../DashboardCommon/dashboardCards';
import {SuppressedValue} from '../../DashboardCommon/SuppressedValue.comp';

import {fmtValue, benchmarkText} from '../../DashboardCommon/units';
// "Grade # · Class Name" (source label format) -> "Class Name (Grade #)".
// [^\w]* (any non-word separator) instead of a specific character class —
// the real data's separator glyph isn't guaranteed to be a literal middot,
// and a mismatch would otherwise leave classPart empty, making the class
// name disappear entirely. Falls back to the original label whenever the
// parse doesn't yield a non-empty class name.
const formatClassLabel = (label) => {
    if (!label) return label;
    // The datahouse label is already "Class Name (Grade #)" ("Grade 5 - Class A
    // (Grade 5)"); re-parsing it appended the grade a second time.
    if (/\(\s*Grade\s*\d+\s*\)\s*$/i.test(String(label))) return label;
    const match = String(label).match(/^\s*(Grade\s*\d+)\s*[^\w]*\s*(.+)$/i);
    if (!match) return label;
    const [, gradePart, classPart] = match;
    const trimmedClass = classPart.trim();
    if (!trimmedClass) return label;
    return `${trimmedClass} (${gradePart.trim()})`;
};

const ClassRow = ({name, score, gradeAvg, band, suppressed}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2.5}}>
        <Box sx={{width: {xs: 120, md: 150}, flexShrink: 0}}>
            <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY}}>{name}</Typography>
        </Box>
        {suppressed ? <Box sx={{flex: 1}}><SuppressedValue/></Box> : (<>
        {/* Bar track with grade-average tick */}
        <Box sx={{flex: 1, position: 'relative', height: 16, backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', borderRadius: '8px'}}>
            <Tooltip title={fmtValue(score)} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${score === null ? 0 : score}%`, backgroundColor: contextBandColor(band), borderRadius: '8px', cursor: 'pointer'}}/>
            </Tooltip>
            {gradeAvg != null && (
                <Tooltip title={benchmarkText('Grade avg', gradeAvg)} arrow placement="top">
                    <Box sx={{
                        position: 'absolute', left: `${gradeAvg}%`, top: '-9px', width: 0, height: 0,
                        borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                        borderTop: `9px solid ${MARKER_PRIMARY}`,
                        transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                    }}/>
                </Tooltip>
            )}
        </Box>
        <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, width: 34, textAlign: 'right', flexShrink: 0}}>{fmtValue(score)}</Typography>
        <Box sx={{flexShrink: 0}}>
            {band && <BandChip label={band} color={contextBandColor(band)}/>}
        </Box>
        </>)}
    </Box>
);

const CONTEXT_CLASS_SCORES_SECTION_CODE = 'S_6d';
const CARD_SECTION = CONTEXT_CLASS_SCORES_SECTION_CODE;

const ContextClassScoresTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    const s8Section = dashboardData?.cards?.sections?.find(s => s.section_code === CONTEXT_CLASS_SCORES_SECTION_CODE);
    if (!s8Section) {
        return (
            <NoDataAvailable />
        );
    }

    const { cards } = s8Section;
    const titleCard = cards?.find(c => c.card_type === 'TitleCard');
    const barChartCard = cards?.find(c => c.card_type === 'BarChartBMCard');

    // Fixed title per the mockup (SD slide 70), not the card's "Class-Level Scores — Grade #".
    const headerTitle = "Context & Support by Class";
    const headerSubtitle = titleCard?.footer || "Which classes would benefit most from added support?";

    const items = barChartCard?.items || [];
    // The grade tick is the class's own grade cell; a grade under the n floor (one small class, or a
    // scope-suppressed school) arrives flagged and draws no tick.
    const gradeAvgOf = (item) => {
        const b = (item.benchmark_values || []).find((x) => /grade/i.test(x.name || ''));
        return !b || isSuppressedBenchmark(b) || b.value === undefined || b.value === null ? null : Math.round(b.value);
    };

    // The guidance names the lowest SHOWN class; classes under the n floor take no part.
    const scored = items.filter((it) => !isSuppressedItem(it) && whole(it.value) !== null);
    const lowestItem = scored.length ? [...scored].sort((a, b) => whole(a.value) - whole(b.value))[0] : null;
    const bannerText = lowestItem
        ? `Class differences may reflect class size or coverage. Use ${formatClassLabel(lowestItem.label)} to target support, not to compare teachers.`
        : (items.length ? 'Class scores are not shown for classes with fewer than 10 students.' : 'Class differences may reflect class size or coverage. Use the lowest class to target support, not to compare teachers.');

    const mappedClasses = items.map(item => {
        const suppressed = isSuppressedItem(item);
        const score = suppressed ? null : whole(item.value);
        const band = suppressed ? null : (item.band || item.data?.band || null);
        return {
            name: formatClassLabel(item.label),
            score,
            gradeAvg: gradeAvgOf(item),
            band,
            suppressed,
        };
    });

    return (
        <Box>
            <PageHeader
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <BandMarkerLegend bandColors={CONTEXT_BAND_COLORS} markerLabel="Grade avg"/>
                {mappedClasses.map((c) => <ClassRow key={c.name} {...c}/>)}
            </Box>

            <GuidanceBanner label="Recommended action:" text={bannerText}/>
        </Box>
    );
};

export default ContextClassScoresTab;
